import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { US_STATES } from "@/lib/us-states";
import { Plus, Trash2, Star } from "lucide-react";

const MAX_ADDRESSES = 5;
const EMPTY = { label: "Lab", name: "", org: "", line1: "", line2: "", city: "", state: "", zip: "", country: "US", phone: "", is_default: false };

export default function AccountAddresses() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/account/addresses").then((r) => { setItems(r.data); setLoading(false); }).catch(() => setLoading(false));
  };
  useEffect(load, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this address?")) return;
    try { await api.delete(`/account/addresses/${id}`); toast.success("Address removed"); load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Delete failed"); }
  };

  const setDefault = async (a) => {
    try {
      await api.patch(`/account/addresses/${a.id}`, { ...a, is_default: true });
      toast.success("Default address updated");
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Update failed"); }
  };

  return (
    <div data-testid="account-addresses">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-display text-2xl tracking-tight">Saved addresses</h2>
          <p className="text-xs text-[var(--ink-muted)] mt-1 font-mono uppercase tracking-wider">
            {items.length}/{MAX_ADDRESSES} saved
          </p>
        </div>
        <button
          onClick={() => setAdding(true)}
          disabled={items.length >= MAX_ADDRESSES}
          className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
          data-testid="add-address-btn"
        >
          <Plus className="h-4 w-4" /> Add address
        </button>
      </div>
      {loading && <p className="text-sm text-[var(--ink-muted)]">Loading…</p>}
      {!loading && items.length === 0 && (
        <div className="border border-dashed border-[var(--line)] rounded-md p-8 text-center text-sm text-[var(--ink-muted)]" data-testid="addresses-empty">
          You haven't saved any addresses yet. Add your institution's shipping address so it auto-fills at checkout.
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.map((a) => (
          <div key={a.id} className="border border-[var(--line)] rounded-md p-5 bg-[var(--paper-2)] relative" data-testid={`addr-card-${a.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="font-mono text-[10.5px] tracking-[0.18em] uppercase text-[var(--terra)]">
                  {a.label || "Address"}{a.is_default && " · Default"}
                </div>
                <div className="mt-2 text-sm">
                  <div className="font-semibold">{a.name}</div>
                  {a.org && <div className="text-[var(--ink-soft)]">{a.org}</div>}
                  <div className="font-mono text-xs text-[var(--ink-soft)] mt-1 leading-relaxed">
                    {a.line1}{a.line2 ? <>, {a.line2}</> : null}<br />
                    {a.city}, {a.state} {a.zip}<br />
                    {a.country}
                    {a.phone && <><br />☎ {a.phone}</>}
                  </div>
                </div>
              </div>
              <div className="flex flex-col items-end gap-2">
                {!a.is_default && (
                  <button
                    onClick={() => setDefault(a)}
                    className="text-[11px] font-mono uppercase tracking-wider text-[var(--ink-muted)] hover:text-[var(--terra)] inline-flex items-center gap-1"
                    data-testid={`default-addr-${a.id}`}
                    title="Set as default"
                  >
                    <Star className="h-3.5 w-3.5" /> Default
                  </button>
                )}
                <button
                  onClick={() => remove(a.id)}
                  className="text-[var(--rust)] hover:opacity-80"
                  aria-label="Delete"
                  data-testid={`delete-addr-${a.id}`}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {adding && <AddModal onClose={() => setAdding(false)} onSaved={() => { setAdding(false); load(); }} />}
    </div>
  );
}

function AddModal({ onClose, onSaved }) {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const onF = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const save = async () => {
    if (!f.name || !f.line1 || !f.city || !f.state || !f.zip) {
      toast.error("Recipient, address line 1, city, state, and zip are required.");
      return;
    }
    const digits = (f.phone || "").replace(/\D/g, "");
    if (digits.length < 7) {
      toast.error("Phone number is required (used by the carrier for delivery).");
      return;
    }
    setBusy(true);
    try {
      await api.post("/account/addresses", f);
      toast.success("Address saved");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm grid place-items-center z-50 p-4" data-testid="add-address-modal" onClick={onClose}>
      <div className="bg-[var(--paper)] border border-[var(--line)] rounded-md w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
        <div className="eyebrow">New address</div>
        <h3 className="font-display text-xl mt-1 mb-4">Save a shipping address</h3>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <Field label="Label" v={f.label} onC={onF("label")} testid="addr-label" placeholder="Lab / Home / Warehouse" />
          <Field label="Country" v={f.country} onC={onF("country")} testid="addr-country" />
          <Field label="Recipient name *" v={f.name} onC={onF("name")} testid="addr-name" full />
          <Field label="Organization" v={f.org} onC={onF("org")} testid="addr-org" full />
          <Field label="Address line 1 *" v={f.line1} onC={onF("line1")} testid="addr-line1" full />
          <Field label="Address line 2" v={f.line2} onC={onF("line2")} testid="addr-line2" full />
          <Field label="City *" v={f.city} onC={onF("city")} testid="addr-city" />
          <label className="text-xs">
            <span className="block text-[10.5px] tracking-[0.18em] uppercase text-[var(--ink-muted)] mb-1 font-mono">State *</span>
            <select
              value={f.state || ""}
              onChange={onF("state")}
              className="w-full px-3 py-2 border border-[var(--line)] bg-[var(--paper)] text-sm rounded-md outline-none focus:border-[var(--terra)]"
              data-testid="addr-state"
            >
              <option value="">Select a state</option>
              {US_STATES.map((s) => (
                <option key={s.code} value={s.code}>{s.name}</option>
              ))}
            </select>
          </label>
          <Field label="ZIP / Postal code *" v={f.zip} onC={onF("zip")} testid="addr-zip" />
          <Field label="Phone (required · carrier delivery) *" v={f.phone} onC={onF("phone")} testid="addr-phone" placeholder="+1 555 123 4567" full />
          <label className="col-span-2 text-sm flex items-center gap-2 mt-1 cursor-pointer">
            <input type="checkbox" checked={f.is_default} onChange={onF("is_default")} data-testid="addr-default" className="h-4 w-4 accent-[var(--terra)]" />
            <span>Make this my default address</span>
          </label>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} className="btn-secondary" data-testid="addr-cancel">Cancel</button>
          <button onClick={save} disabled={busy} className="btn-primary" data-testid="addr-save">
            {busy ? "Saving…" : "Save address"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, v, onC, testid, full, placeholder }) {
  return (
    <label className={`text-xs ${full ? "col-span-2" : ""}`}>
      <span className="block text-[10.5px] tracking-[0.18em] uppercase text-[var(--ink-muted)] mb-1 font-mono">{label}</span>
      <input value={v || ""} onChange={onC} placeholder={placeholder} className="w-full px-3 py-2 border border-[var(--line)] bg-[var(--paper)] text-sm rounded-md outline-none focus:border-[var(--terra)]" data-testid={testid} />
    </label>
  );
}
